import { Pagination, PaginationProps, Table, TableProps } from 'antd'
import { ColumnsType } from 'antd/lib/table'
import styled from 'styled-components'

import { themes } from '@theme'
import { useMemo } from 'react'
import { RowClassName } from 'rc-table/lib/interface'

interface ITableProps {
  columns: ColumnsType<any>
  dataSource: any
  key?: number | string
  rowKey?: any
  paginationProps?: PaginationProps
  onRowClick?: (record: any) => void
  sticky?: boolean
  totalResults?: number
  className?: string
  tableProps?: TableProps<any>
  hasIndexColumn?: boolean
  rowClassName?: string | RowClassName<any>
}

export const SharedTable = ({
  columns,
  dataSource,
  key,
  rowKey,
  paginationProps,
  onRowClick,
  sticky,
  totalResults,
  className,
  hasIndexColumn = true,
  tableProps,
  rowClassName,
}: ITableProps) => {
  const { current: pageNumber, pageSize } = paginationProps || {}
  const handleRowClick = (record: any) => {
    if (onRowClick) {
      onRowClick(record)
    }
  }

  const dataTable = useMemo(() => {
    if (!Array.isArray(dataSource) || !dataSource?.length) return []

    return dataSource.map((it, idx) => ({
      ...it,
      // If the list showing the NO (index) column --> using columns[0] because first column in system always display NO value
      ...(hasIndexColumn && {
        [(columns as any)?.[0]?.dataIndex]:
          pageNumber && pageSize
            ? idx + 1 + (Number(pageNumber) - 1) * Number(pageSize)
            : idx + 1,
      }),
    }))
  }, [columns, dataSource, hasIndexColumn, pageNumber, pageSize])

  return (
    <StyledTableWrapper $appTheme={themes.theme.light.colors.primary}>
      <Table
        className={`bg-gray-100 md:ml-3 mt-3 ${className}`}
        key={key}
        rowKey={rowKey}
        columns={columns}
        pagination={false}
        dataSource={dataTable}
        rowClassName={
          rowClassName
            ? rowClassName
            : (record, index) =>
                index % 2 === 0 ? 'table-row-light' : 'table-row-dark'
        }
        onRow={(record, rowIndex) => {
          return {
            onClick: () => handleRowClick(record),
          }
        }}
        sticky={sticky}
        {...tableProps}
      />
      <div
        className={`flex ${
          !!totalResults ? 'justify-between' : 'justify-end'
        } mt-3 px-[0.5rem]`}
      >
        {!!totalResults ? (
          <span className="font-semibold p-2 border border-neutral-300">
            {totalResults} {totalResults > 1 ? 'results' : 'result'}
          </span>
        ) : null}
        {paginationProps?.total && <Pagination {...paginationProps} />}
      </div>
    </StyledTableWrapper>
  )
}

const StyledTableWrapper = styled.div<{
  $appTheme: string
}>`
  .ant-table-tbody > tr:hover {
    cursor: pointer;
  }

  .ant-pagination-item-active a {
    border-color: ${(p) => p.$appTheme};
  }
  .ant-pagination-item-active {
    font-weight: 500;
    background: #fff;
    border-color: ${(p) => p.$appTheme};
    a {
      color: ${(p) => p.$appTheme};
    }
  }

  .ant-table-thead > tr > th {
    position: relative;
    color: white;
    font-weight: 500;
    text-align: left;
    background: ${(p) => p.$appTheme};
    border-bottom: 2px solid #f0f0f0;
    transition: background 0.3s ease;
    padding: 0.75rem 1.25rem;
    font-size: 0.875rem;
  }
  .ant-table-tbody > tr > td {
    transition: background 0.3s;
    text-align: left;
    padding: 0.75rem 1.25rem;
    font-size: 0.875rem;
  }
  .ant-table-wrapper {
    overflow: auto;
    width: 100%;
    max-height: 63vh;
  }
  .ant-table-wrapper .ant-table {
    width: 100%;
  }

  .ant-table-thead > tr > th,
  .ant-table-tbody > tr > td {
    width: auto;
    white-space: nowrap;
  }
  .ant-table-content > table {
    width: 100%;
    border-left: 2px solid #f0f0f0;
  }
  .ant-table-content {
    margin-right: 1.25rem;
  }

  .ant-table-thead {
    .ant-table-cell {
      word-break: auto-phrase;
      text-wrap: wrap;
      overflow-wrap: normal;
    }
  }

  .ant-pagination {
    display: flex;
    align-items: center;
    justify-content: center;
    .ant-pagination-prev {
      button {
        display: flex;
        align-items: center;
        justify-content: center;
      }

      &:hover,
      &:focus-visible {
        .ant-pagination-item-link {
          border-color: ${(p) => p.$appTheme};
          color: ${(p) => p.$appTheme};
        }
      }
    }
    .ant-pagination-next {
      button {
        display: flex;
        align-items: center;
        justify-content: center;
      }

      &:hover,
      &:focus-visible {
        .ant-pagination-item-link {
          border-color: ${(p) => p.$appTheme};
          color: ${(p) => p.$appTheme};
        }
      }
    }

    .ant-pagination-disabled {
      .ant-pagination-item-link {
        color: rgba(0, 0, 0, 0.25);
        border-color: #d9d9d9;
        cursor: not-allowed;
      }

      &:hover {
        .ant-pagination-item-link {
          color: rgba(0, 0, 0, 0.25);
          border-color: #d9d9d9;
          cursor: not-allowed;
        }
      }
    }

    .ant-pagination-item {
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .ant-pagination-item:hover {
      border-color: ${(p) => p.$appTheme};
      a {
        color: ${(p) => p.$appTheme};
      }
    }

    .ant-table-thead
      > tr
      > th:not(:last-child):not(.ant-table-selection-column):not(.ant-table-row-expand-icon-cell):not([colspan]):before {
      position: absolute;
      top: 50%;
      right: 0;
      width: 1px;
      height: 1.6em;
      background-color: white !important;
      transform: translateY(-50%);
      transition: background-color 0.3s;
      content: '';
    }
  }
`
